// ./src/pages/private/EventGamesPage/presets.ts
//
// A client-side copy of the role presets (mirrors eventsmagic-api
// src/boundary/eventGame.ts EVENT_GAME_PRESETS). Used to PREFILL a new game's
// name + roles in the config drawer; the server also records `game.preset`.
// Keep these in sync with the BE preset library when it changes.

import { EventGameMarker, EventGameRevealRule, EventGameAssignmentKind } from "@/utils/api";

export interface GamePreset {
  id: string;
  label: string;
  description: string;
  // Omitted → "role" (deal a marker). "target" = deal another player (Secret Santa).
  assignmentKind?: EventGameAssignmentKind;
  revealRule: EventGameRevealRule;
  minPlayers: number;
  markers: Omit<EventGameMarker, "id" | "gameId" | "eventId">[];
  // Self-mark checklist games (Human Bingo / scavenger): the starting card.
  checklistTemplate?: { items: { id: string; label: string }[]; allowReuse?: boolean };
  // Target / pair games: default game rules stored on winRules. `matchInterests` is
  // pair-only (Networking Roulette — prefer partners who share interests).
  winRules?: { excludeHousehold?: boolean; matchInterests?: boolean };
}

export const GAME_PRESETS: GamePreset[] = [
  {
    id: "mafia",
    label: "Mafia / Werewolf",
    description: "Town vs Mafia — night eliminations, day accusations. Mafia scale ~1 in 4.",
    revealRule: "on_elimination",
    minPlayers: 5,
    markers: [
      { name: "Mafia", color: "#e5484d", icon: "🔪", team: "mafia", visibility: "self", composition: { type: "ratio", value: 0.25 } },
      { name: "Detective", color: "#3b82c4", icon: "🔍", team: "town", visibility: "self", composition: { type: "fixed", value: 1 } },
      { name: "Doctor", color: "#12a594", icon: "➕", team: "town", visibility: "self", composition: { type: "fixed", value: 1 } },
      { name: "Civilian", color: "#7b7688", icon: "👤", team: "town", visibility: "self", composition: { type: "rest" } },
    ],
  },
  {
    id: "imposter",
    label: "Imposter",
    description: "Crewmates vs a hidden imposter or two.",
    revealRule: "on_elimination",
    minPlayers: 4,
    markers: [
      { name: "Imposter", color: "#e5484d", icon: "🚀", team: "imposter", visibility: "self", composition: { type: "ratio", value: 0.2 } },
      { name: "Crewmate", color: "#3b82c4", icon: "🧑‍🚀", team: "crew", visibility: "self", composition: { type: "rest" } },
    ],
  },
  {
    id: "onenight",
    label: "One Night Werewolf",
    description: "A single night — one deal, one vote, instant reveal.",
    revealRule: "at_end",
    minPlayers: 3,
    markers: [
      { name: "Werewolf", color: "#e5484d", icon: "🐺", team: "wolves", visibility: "self", composition: { type: "ratio", value: 0.25 } },
      { name: "Seer", color: "#3b82c4", icon: "🔮", team: "village", visibility: "self", composition: { type: "fixed", value: 1 } },
      { name: "Villager", color: "#7b7688", icon: "👤", team: "village", visibility: "self", composition: { type: "rest" } },
    ],
  },
  {
    // "Murder at Ravensworth Manor" — every guest is dealt a unique character whose
    // secret dossier (backstory / secret / objectives / relationships) lives in the
    // marker `payload` and shows only on their own "My Role" block. Extra players
    // become a generic "Party Guest". Keep in sync with the BE preset library.
    id: "murdermystery",
    label: "Murder Mystery Dinner",
    description: "A 1920s manor dinner party — every guest plays a suspect with a secret dossier. Works with 5–8 players; seat all 8 characters for the full plot.",
    revealRule: "at_end",
    minPlayers: 4,
    // NOTE: order = deal priority. buildSlots deals fixed roles in this order until
    // players run out, so the murder-critical characters (host, murderer, key
    // suspects) come first — a smaller party still gets a solvable mystery.
    markers: [
      {
        name: "Lord Edmund Ravensworth", color: "#b8860b", icon: "🎩", team: "", visibility: "self", composition: { type: "fixed", value: 1 },
        payload: {
          flavor: "The manor's master — gracious host, drowning in secret debt.",
          backstory: "You inherited Ravensworth Manor along with a mountain of debt. Tonight's lavish party is a desperate performance to keep up appearances.",
          secret: "You are secretly bankrupt. If anyone learns the estate is mortgaged to the hilt, you are ruined.",
          objectives: ["Charm every guest and keep the evening glittering.", "Find out who has been spreading rumours about your finances.", "Avoid being cornered by Mr. Crowe."],
          relationships: ["Married to Lady Vivienne, though you have grown distant.", "You owe Sebastian Crowe a fortune."],
        },
      },
      {
        name: "Mr. Sebastian Crowe", color: "#a01a1a", icon: "🖋️", team: "", visibility: "self", composition: { type: "fixed", value: 1 },
        payload: {
          flavor: "The host's business partner — patient, precise, and owed a great deal.",
          backstory: "You bankrolled Edmund's schemes for years. Tonight the debts come due, one way or another.",
          secret: "You are the murderer. Edmund's death erases your losses and hands you the estate. Play innocent — deflect, accuse, and never let them see your hand.",
          objectives: ["Cast suspicion on Colonel Sterling or Dr. Hartwell.", "Account for your whereabouts convincingly.", "Retrieve the incriminating contract before anyone finds it."],
          relationships: ["Business partner and secret creditor to Lord Edmund."],
        },
      },
      {
        name: "Lady Vivienne Ravensworth", color: "#6d4aff", icon: "💎", team: "", visibility: "self", composition: { type: "fixed", value: 1 },
        payload: {
          flavor: "Elegant and poised — and quietly planning her escape.",
          backstory: "Years of a cold marriage have left you longing for freedom, and you have made... arrangements.",
          secret: "You intend to leave Edmund tonight and start anew — with someone else's help.",
          objectives: ["Slip away for a private word with Dr. Hartwell.", "Keep your travel plans hidden.", "Learn what Mrs. Fenwick has seen."],
          relationships: ["Married to Lord Edmund.", "You confide in Dr. Hartwell more than you should."],
        },
      },
      {
        name: "Colonel Reginald Sterling", color: "#3b82c4", icon: "🎖️", team: "", visibility: "self", composition: { type: "fixed", value: 1 },
        payload: {
          flavor: "A decorated soldier who trades in other people's secrets.",
          backstory: "War made you cunning; peacetime made you poor. You have found that secrets pay better than medals.",
          secret: "You are blackmailing at least two guests here tonight.",
          objectives: ["Collect what Miss Ashford owes you.", "Find fresh leverage on your host.", "Never let anyone glimpse your ledger."],
          relationships: ["Blackmailing Cora Ashford.", "An old \"friend\" of Lord Edmund."],
        },
      },
      {
        name: "Dr. Julian Hartwell", color: "#12a594", icon: "🩺", team: "", visibility: "self", composition: { type: "fixed", value: 1 },
        payload: {
          flavor: "The family physician who knows where every skeleton is buried.",
          backstory: "You have treated this family for twenty years and heard every confession that came with the fever.",
          secret: "You have been quietly in love with Lady Vivienne for years.",
          objectives: ["Protect Vivienne at any cost.", "Keep your patients' secrets — and use them.", "Deflect any suspicion from yourself."],
          relationships: ["Devoted to Lady Vivienne.", "You know the Colonel's true nature."],
        },
      },
      {
        name: "Miss Cora Ashford", color: "#e5484d", icon: "🎬", team: "", visibility: "self", composition: { type: "fixed", value: 1 },
        payload: {
          flavor: "A rising actress with her eyes on a far bigger stage.",
          backstory: "You clawed your way out of nothing and will not let anyone push you back down.",
          secret: "You were once caught stealing — and the Colonel has the proof.",
          objectives: ["Win over Lord Edmund as a patron.", "Persuade the Colonel to destroy the evidence.", "Outshine every other woman in the room."],
          relationships: ["Being blackmailed by Colonel Sterling."],
        },
      },
      {
        name: "Mrs. Beatrice Fenwick", color: "#7b7688", icon: "🔑", team: "", visibility: "self", composition: { type: "fixed", value: 1 },
        payload: {
          flavor: "The housekeeper who sees everything and says nothing — until now.",
          backstory: "Thirty years in service. You have watched this family rise and rot from the servants' stairs.",
          secret: "You witnessed something tonight you were never meant to see.",
          objectives: ["Decide whose secret is worth the most.", "Stay unnoticed while noticing all.", "Protect the household's reputation — or profit from its collapse."],
          relationships: ["Loyal, in your own way, to Lady Vivienne."],
        },
      },
      {
        name: "Miss Delphine Laurent", color: "#c76e9f", icon: "🕊️", team: "", visibility: "self", composition: { type: "fixed", value: 1 },
        payload: {
          flavor: "A mysterious guest from abroad no one quite remembers inviting.",
          backstory: "You arrived with impeccable manners and no clear reason. You are here for one thing only.",
          secret: "You are a private investigator, hired anonymously to watch this family.",
          objectives: ["Discover who among the guests is desperate enough to kill.", "Keep your true profession hidden.", "Report what you learn — to a client you will not name."],
          relationships: ["A stranger to all — which is exactly how you like it."],
        },
      },
      {
        name: "Party Guest", color: "#9aa0a6", icon: "🥂", team: "", visibility: "self", composition: { type: "rest" },
        payload: {
          flavor: "A guest at Ravensworth Manor tonight — mingle, gossip, and enjoy the intrigue.",
          backstory: "You have no secret of your own, but a sharp eye and a loose tongue can steer the whole evening.",
          objectives: ["Question the suspects and share what you learn.", "Help (or hinder) the investigation.", "Enjoy the party."],
        },
      },
    ],
  },
  {
    // Spyfall — a SHARED-SECRET game. Everyone but one Spy shares a secret location,
    // picked at random per round from the "Agent" marker's `secretPool` (the BE strips
    // the pool from each snapshot; a phone only sees its own location). Keep in sync with BE.
    id: "spyfall",
    label: "Spyfall",
    description: "Everyone shares a secret location — except one Spy who must blend in and guess it. New location each round. Best with 4–10.",
    revealRule: "at_end",
    minPlayers: 4,
    markers: [
      {
        name: "Spy", color: "#e5484d", icon: "🕵️", team: "spy", visibility: "self", composition: { type: "fixed", value: 1 },
        payload: { flavor: "You are the Spy. You do NOT know the secret location. Listen, blend in, and work out where everyone is — before they unmask you." },
      },
      {
        name: "Agent", color: "#3b82c4", icon: "🕶️", team: "agents", visibility: "self", composition: { type: "rest" },
        payload: {
          flavor: "Everyone here (except one hidden Spy) shares the same secret location. Prove you belong with your answers — and expose the Spy.",
          secretLabel: "Secret location",
          secretPool: [
            "Casino", "Passenger airplane", "Beach resort", "Hospital", "Submarine", "Movie set", "Cruise ship",
            "University campus", "Restaurant kitchen", "Space station", "Bank vault", "Overnight sleeper train",
            "Ski resort", "Art museum", "Wedding reception", "Football stadium", "Pirate ship", "Hotel lobby",
            "Zoo", "Theatre backstage", "Police station", "Vineyard",
          ],
        },
      },
    ],
  },
  {
    // Undercover — a SHARED-SECRET word game. Most players get the "civilian" word; a
    // hidden Undercover gets a related-but-different word from the SAME pair (pool on the
    // "Civilian" marker; each marker takes its slot via `secretKey`). Keep in sync with BE.
    id: "undercover",
    label: "Undercover",
    description: "Most players share a secret word; a hidden Undercover has a similar-but-different one. Describe it without saying it, and root out the impostor. Best with 4–10.",
    revealRule: "at_end",
    minPlayers: 4,
    markers: [
      {
        name: "Undercover", color: "#e5484d", icon: "🎭", team: "undercover", visibility: "self", composition: { type: "fixed", value: 1 },
        payload: { flavor: "Your word is slightly DIFFERENT from most players. Describe it without saying it — and blend in so no one catches on.", secretLabel: "Your word", secretKey: "undercover" },
      },
      {
        name: "Civilian", color: "#3b82c4", icon: "🧑", team: "civilians", visibility: "self", composition: { type: "rest" },
        payload: {
          flavor: "Most players share YOUR word. Describe it without saying it — and find the Undercover whose word is a little off.",
          secretLabel: "Your word",
          secretKey: "civilian",
          secretPool: [
            { civilian: "Coffee", undercover: "Tea" }, { civilian: "Dog", undercover: "Wolf" }, { civilian: "Cinema", undercover: "Theatre" },
            { civilian: "Guitar", undercover: "Ukulele" }, { civilian: "Ocean", undercover: "Lake" }, { civilian: "Pizza", undercover: "Flatbread" },
            { civilian: "Train", undercover: "Tram" }, { civilian: "Butterfly", undercover: "Moth" }, { civilian: "Jam", undercover: "Jelly" },
            { civilian: "Piano", undercover: "Keyboard" }, { civilian: "Sunglasses", undercover: "Reading glasses" }, { civilian: "Football", undercover: "Rugby" },
            { civilian: "Ice cream", undercover: "Frozen yoghurt" }, { civilian: "Castle", undercover: "Palace" }, { civilian: "Snowman", undercover: "Ice sculpture" },
          ],
        },
      },
    ],
  },
  {
    // Human Bingo — a self-mark mingling icebreaker. Everyone gets the SAME card; guests
    // mark a square by scanning that person's badge (or typing their name). One "Player"
    // role so runRound deals to everyone; the card lives on the game's checklistTemplate.
    id: "humanbingo",
    label: "Human Bingo",
    description: "A mingling icebreaker — find a guest who matches each square and mark it (scan their code or type their name). First to complete the card wins.",
    revealRule: "never",
    minPlayers: 4,
    markers: [
      { name: "Player", color: "#6d4aff", icon: "🎉", team: "", visibility: "all", composition: { type: "rest" } },
    ],
    checklistTemplate: {
      allowReuse: false,
      items: [
        { id: "travel", label: "Has visited 3+ countries" },
        { id: "langs", label: "Speaks 2+ languages" },
        { id: "instrument", label: "Plays a musical instrument" },
        { id: "pet", label: "Has a pet at home" },
        { id: "runs", label: "Runs or cycles regularly" },
        { id: "birthmonth", label: "Shares your birthday month" },
        { id: "sushi", label: "Loves sushi" },
        { id: "famous", label: "Has met someone famous" },
        { id: "lefty", label: "Is left-handed" },
        { id: "tattoo", label: "Has a tattoo" },
        { id: "juggle", label: "Can juggle" },
        { id: "veggie", label: "Is vegetarian or vegan" },
        { id: "skydive", label: "Has been skydiving" },
        { id: "samename", label: "Has the same first-name initial as you" },
        { id: "earlybird", label: "Woke up before 6am today" },
        { id: "gamer", label: "Plays video games" },
      ],
    },
  },
  {
    // Secret Santa — the TARGET-kind game: every player is secretly dealt another PLAYER
    // to gift (assignmentKind "target"), not a role. The server deals one random gift-chain
    // (nobody draws themselves; no mutual pairs) and can keep households (rsvp_group) apart.
    // Each player privately sees who they drew on their own "My Role" card. Keep in sync with BE.
    id: "secretsanta",
    label: "Secret Santa",
    description: "Everyone secretly draws another guest to give a gift to — no repeats, nobody gets themselves, and (optionally) no one draws their own household. Each player privately sees who they got.",
    assignmentKind: "target",
    revealRule: "never",
    minPlayers: 3,
    winRules: { excludeHousehold: true },
    markers: [
      {
        name: "Secret Santa", color: "#c0392b", icon: "🎁", team: "", visibility: "self", composition: { type: "rest" },
        payload: { flavor: "Keep it a secret! Bring a small gift for the guest below within the agreed budget." },
      },
    ],
  },
  {
    // Assassin — the TARGET primitive with elimination. Same single gift-chain deal as Secret Santa, but reveal
    // 'on_elimination': the caught player self-reports, their assassin inherits the target, last one wins. Target
    // stays private; only eliminations are public (the "still alive" scoreboard). Keep in sync with the BE preset.
    id: "assassin",
    label: "Assassin",
    description: 'Everyone secretly draws a target to "eliminate" (a tag / a silly secret task). Get your target and you inherit theirs — last one standing wins. Your target is private; only eliminations are public.',
    assignmentKind: "target",
    revealRule: "on_elimination",
    minPlayers: 3,
    winRules: { excludeHousehold: false },
    markers: [
      {
        name: "Assassin", color: "#b91c1c", icon: "🗡️", team: "", visibility: "self", composition: { type: "rest" },
        payload: { flavor: "Discreetly “eliminate” your target — a tag, a secret handshake, whatever the host set. When you get them, you inherit their target. Don’t get caught first!" },
      },
    ],
  },
  {
    // Who Am I? / Forehead game — the INVERSE-REVEAL game (marker visibility "others"): each
    // player is dealt a secret identity they can't see but everyone else can. One "Mystery
    // Identity" marker carries an identityPool (a cast of names); runRound deals each player a
    // unique one, hidden from its owner. Keep the identityPool in sync with the BE preset.
    id: "whoami",
    label: "Who Am I?",
    description: "Everyone is secretly given a famous person or character they can’t see — but everyone else can. Ask yes/no questions to work out who you are. A classic icebreaker.",
    revealRule: "at_end",
    minPlayers: 3,
    markers: [
      {
        name: "Mystery Identity", color: "#8b5cf6", icon: "🎭", team: "", visibility: "others", composition: { type: "rest" },
        payload: {
          flavor: "Everyone can see who you are — except you! Ask the room yes/no questions to work out your identity.",
          identityLabel: "Identity",
          identityPool: [
            "Beyoncé", "Albert Einstein", "Sherlock Holmes", "Cleopatra", "Batman", "Taylor Swift", "Leonardo da Vinci",
            "Harry Potter", "Marie Curie", "Elvis Presley", "Yoda", "Frida Kahlo", "James Bond", "Oprah Winfrey",
            "Napoleon Bonaparte", "Wonder Woman", "Charlie Chaplin", "Serena Williams", "Mickey Mouse", "Nelson Mandela",
            "Lady Gaga", "Homer Simpson", "Amelia Earhart", "Darth Vader",
          ],
        },
      },
    ],
  },
  {
    // Networking Roulette — the PAIR-kind game (assignmentKind "pair"): each round every
    // player is matched with another (mutual — a trio for the odd one out). The server avoids
    // re-pairing across rounds and, with matchInterests on, prefers partners who share interests
    // (from each guest's own "My interests" card). Deal a new round to reshuffle. Keep in sync with BE.
    id: "networking",
    label: "Networking Roulette",
    description: "Every round, each guest is matched with a new person to meet — never the same partner twice (until everyone has met), and optionally paired by shared interests. Great structured mingling for conferences and mixers.",
    assignmentKind: "pair",
    revealRule: "never",
    minPlayers: 2,
    winRules: { excludeHousehold: true, matchInterests: true },
    markers: [
      {
        name: "Networker", color: "#0ea5e9", icon: "🤝", team: "", visibility: "self", composition: { type: "rest" },
        payload: { flavor: "Go find the person below and introduce yourself! A new partner every round." },
      },
    ],
  },
];

export const findPreset = (id?: string | null): GamePreset | null => GAME_PRESETS.find((p) => p.id === id) ?? null;
